import { useMutation } from '@apollo/client';
import React, { FC, Fragment } from 'react';
import { Formik, FormikHelpers as FormikActions, FormikProps } from 'formik';
import { object, date } from 'yup';
import { isEqual, isWithinInterval, Interval, parseISO, formatISO } from 'date-fns';
import { map, min, compact, max, find } from 'lodash';
import {
  UpdateWorkPackageScheduleInput,
  UpdateWorkPackageSchedulePayload,
  WorkPackage,
  PersonaType,
} from '@bechtel/types';
import {
  Box,
  Button,
  ButtonAppearance,
  SideSheet,
  SideSheetClose,
  SideSheetHeader,
  SideSheetFooter,
  SideSheetBody,
  SideSheetSubHeader,
  SideSheetContentCol,
  SideSheetSize,
  WeekSelector,
  Spinner,
  Text,
  useModalState,
  WeekType,
} from '@bechtel/gds';
import { EditWorkPackageScheduleProps } from './types';
import { UPDATE_WORK_PACKAGE_SCHEDULE } from '@bechtel/client-gql';
import { ConfirmDeletePlan } from './confirm';
import { WorkpackageScheduleError } from './error';
import {
  PlanningActionType,
  usePlanning,
  useAppUser,
  TitleValue,
  updateUnplanCache,
  DND_IDENTIFIER,
  useConfig,
} from '@bechtel/client-common';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';

interface FormikState {
  workPackageIdList?: string[];
  startDate?: Date;
  endDate?: Date;
  onClose?: () => void;
  editedWeekType?: WeekType;
}
const initialValues: FormikState = {
  workPackageIdList: [],
  editedWeekType: WeekType.END,
};

const editScheduleValidationSchema = object().shape({
  startDate: date().required(t`Start date is mandatory.`),
  endDate: date().required(t`End date is mandatory.`),
});

const getWorkpackageForemen = (workPackage: WorkPackage) => {
  const primaryForeman = find(workPackage.primaryOwners, (owner) => owner.persona === PersonaType.FOREMAN)?.crewMember;
  const secondaryForeman = find(
    workPackage.secondaryOwners,
    (owner) => owner.persona === PersonaType.FOREMAN
  )?.crewMember;
  const otherForeman = find(workPackage.otherOwners, (owner) => owner.persona === PersonaType.FOREMAN)?.crewMember;

  return compact([primaryForeman, secondaryForeman, otherForeman]);
};

export const EditWorkPackageSchedule: FC<EditWorkPackageScheduleProps> = ({
  workPackageList,
  workPackageTag,
  onClose,
  startDate,
  endDate,
  isPlanningModule = false,
}) => {
  const { numPlanningNextWeeks } = useConfig();
  const { i18n } = useLingui();
  const { planningDispatch } = usePlanning() || {};
  //Open confirm modal to be activated once backend supports delete plan on update schedule

  const { visible: confirmModalVisible, open: openConfirmModal, close: closeConfirmModal } = useModalState(false);
  const { visible: errorModalVisible, open: openErrorModal, close: closeErrorModal } = useModalState(false);
  const { user } = useAppUser();

  const [updateSchedule, { loading }] = useMutation<
    { updateWorkPackageSchedule: UpdateWorkPackageSchedulePayload },
    { input: UpdateWorkPackageScheduleInput }
  >(UPDATE_WORK_PACKAGE_SCHEDULE, {
    onError: () => {
      openErrorModal();
    },
  });
  /*Check if earliest plan start date and latest plan end date of all planned milestones from the selected workpackages
  is within the new schedule selected*/
  const arePlansWithinSchedule = (startDate: Date, endDate: Date) => {
    const earliestPlanStartDate = min(
      compact(
        map(workPackageList, (workPackage) => {
          return workPackage?.plannedMilestoneStartDate;
        })
      )
    );

    const latestPlanEndDate = max(
      compact(
        map(workPackageList, (workPackage) => {
          return workPackage?.plannedMilestoneEndDate;
        })
      )
    );
    if (!earliestPlanStartDate || !latestPlanEndDate) return true;

    const scheduleInterval: Interval = { start: startDate, end: endDate };
    if (
      isWithinInterval(new Date(earliestPlanStartDate), scheduleInterval) &&
      isWithinInterval(new Date(latestPlanEndDate), scheduleInterval)
    )
      return true;
    return false;
  };
  const refetchQueries = [`plannedMilestones`, `planningWeekStats`, `expiredMilestoneCount`];
  return (
    <SideSheet
      size={SideSheetSize.DEFAULT}
      style={{ width: '590px' }} //Setting width manually to fit the week selector control to correct size
      data-testid={'edit-workpackage-sidesheet'}
      visible
    >
      <SideSheetHeader title={t`Schedule Work Package`}>
        <SideSheetClose onClick={onClose}></SideSheetClose>
      </SideSheetHeader>

      <Formik
        initialValues={initialValues}
        validationSchema={editScheduleValidationSchema}
        validateOnBlur={false}
        validateOnChange={false}
        onSubmit={async (values: FormikState, actions: FormikActions<FormikState>) => {
          if (!values.startDate || !values.endDate) return;
          const { data: workPackageScheduleData } = await updateSchedule({
            variables: {
              input: {
                workPackageIds: map(workPackageList, (workPackage) => {
                  return workPackage.id;
                }),
                startDate: formatISO(values.startDate, { representation: 'date' }),
                endDate: formatISO(values.endDate, { representation: 'date' }),
              },
            },
            update: (cache, { data }) => {
              if (
                data?.updateWorkPackageSchedule?.unplannedMilestones &&
                data?.updateWorkPackageSchedule?.unplannedMilestones.length
              ) {
                map(workPackageList, (workPackage) => {
                  const foremanList = getWorkpackageForemen(workPackage);
                  map(foremanList, (foreman) => {
                    updateUnplanCache(
                      cache,
                      DND_IDENTIFIER.Unplanned,
                      data?.updateWorkPackageSchedule?.unplannedMilestones //Check to avoid typescript warning
                        ? data?.updateWorkPackageSchedule?.unplannedMilestones
                        : [],
                      [workPackage.id],
                      foreman?.id,
                      numPlanningNextWeeks
                    );
                  });
                });
              }
            },
            refetchQueries: isPlanningModule ? refetchQueries : [`workPackages`, ...refetchQueries],
          });
          actions.resetForm({ values: initialValues });
          actions.setSubmitting(false);

          if (
            workPackageScheduleData?.updateWorkPackageSchedule?.erroredWorkPackages &&
            workPackageScheduleData?.updateWorkPackageSchedule?.erroredWorkPackages.length
          ) {
            openErrorModal();
          } else {
            if (isPlanningModule) {
              planningDispatch({
                type: PlanningActionType.ResetWorkPackageSelection,
                payload: { resetWorkPackagePager: true, selectedWpList: [] },
              });
            }
            onClose();
          }
        }}
      >
        {({ submitForm, errors, isValid, dirty, setFieldValue, values }: FormikProps<FormikState>) => {
          return (
            <Fragment>
              <SideSheetSubHeader>
                <TitleValue
                  title={t`Work Package`}
                  value={workPackageList?.length > 1 ? t`Multiple Workpackages` : workPackageTag ? workPackageTag : ''}
                  size="500"
                  data-testid="edit-schedule-sub-header"
                />
              </SideSheetSubHeader>
              <SideSheetBody>
                <SideSheetContentCol>
                  <WeekSelector
                    startDate={startDate ? parseISO(startDate) : undefined}
                    endDate={endDate ? parseISO(endDate) : undefined}
                    onSelect={(startDate, endDate) => {
                      setFieldValue('startDate', startDate);
                      setFieldValue('endDate', endDate);
                      setFieldValue('editedWeekType', WeekType.END);
                    }}
                    weekStartsOn={user?.project?.projectConfiguration.weekStartDay || 0}
                    editedWeekType={!values.startDate && !values.endDate ? WeekType.START : values.editedWeekType}
                  ></WeekSelector>
                  {!(errors && (errors.startDate || errors.endDate)) && (
                    <Box ml={{ sm: '500' }} mt={{ sm: '200' }}>
                      <Text size="400" weight="300" color="grey.1000">
                        {errors?.startDate ? i18n._(String(errors?.startDate)) : ''}
                        {errors?.endDate ? i18n._(String(errors?.endDate)) : ''}
                      </Text>
                    </Box>
                  )}
                </SideSheetContentCol>
              </SideSheetBody>
              <SideSheetFooter>
                <Box justifyContent={{ sm: 'flex-end' }}>
                  <Box mr={{ sm: '400' }}>
                    <Button appearance={ButtonAppearance.SECONDARY} onClick={onClose} disabled={loading}>
                      <Trans>Cancel</Trans>
                    </Button>
                  </Box>
                  <Box>
                    <Button
                      appearance={ButtonAppearance.SECONDARY}
                      onClick={() => {
                        if (values.startDate && values.endDate) {
                          if (!arePlansWithinSchedule(values.startDate, values.endDate)) {
                            openConfirmModal();
                          } else {
                            submitForm();
                          }
                        }
                      }}
                      disabled={
                        !dirty ||
                        !isValid ||
                        loading ||
                        (values.startDate &&
                          values.endDate &&
                          isEqual(new Date(startDate), values.startDate) &&
                          isEqual(new Date(endDate), values.endDate))
                      }
                    >
                      {loading && <Spinner />}
                      {!loading && <Trans>Confirm</Trans>}
                    </Button>
                  </Box>
                </Box>
              </SideSheetFooter>
              {confirmModalVisible && (
                <ConfirmDeletePlan
                  onClose={closeConfirmModal}
                  onConfirm={() => {
                    closeConfirmModal();
                    submitForm();
                  }}
                  workPackageTag={workPackageTag}
                ></ConfirmDeletePlan>
              )}
              {errorModalVisible && <WorkpackageScheduleError onClose={closeErrorModal}></WorkpackageScheduleError>}
            </Fragment>
          );
        }}
      </Formik>
    </SideSheet>
  );
};
